/**
 * Permission Helper Utility
 * Handles colon-based permission system: MODULE:RESOURCE_ACTION
 * 
 * Examples:
 * - ENTITIES:VIEW (module level)
 * - ENTITIES:DETAILS_VIEW (resource level)
 * - ENTITIES:DETAILS_EDIT (action level)
 * - CONTROL_PANEL:SYSTEM_SETTINGS_VIEW (tab level)
 */

/**
 * Check if user has a specific permission
 * Direct check against the permissions array from the database
 */
export function hasPermission(requiredPermission: string, userPermissions: string[]): boolean {
  if (!requiredPermission || !Array.isArray(userPermissions)) return false;

  // Check for super admin
  if (userPermissions.includes('*') || userPermissions.includes('SUPER_ADMIN_PERMISSIONS')) {
    return true;
  }

  // Direct match - check if the required permission exists in the user's permissions
  return userPermissions.includes(requiredPermission);
}

/**
 * Check if user has any of the required permissions
 */
export function hasAnyPermission(requiredPermissions: string[], userPermissions: string[]): boolean {
  if (!Array.isArray(requiredPermissions) || requiredPermissions.length === 0) {
    return true; // No requirements = allowed
  }

  return requiredPermissions.some(perm => hasPermission(perm, userPermissions));
}

/**
 * Check if user has all of the required permissions
 */
export function hasAllPermissions(requiredPermissions: string[], userPermissions: string[]): boolean {
  if (!Array.isArray(requiredPermissions) || requiredPermissions.length === 0) {
    return true; // No requirements = allowed
  }

  return requiredPermissions.every(perm => hasPermission(perm, userPermissions));
}

/**
 * Check if user has access to a module (any permission starting with MODULE:)
 * Uses simple string matching - no parsing needed
 */
export function hasModuleAccess(module: string, userPermissions: string[]): boolean {
  if (!module || !Array.isArray(userPermissions)) return false;

  // Check for super admin
  if (userPermissions.includes('*') || userPermissions.includes('SUPER_ADMIN_PERMISSIONS')) {
    return true;
  }
  
  // Simple string check - check if any permission starts with MODULE:
  return userPermissions.some(perm => perm.startsWith(`${module}:`));
}

/**
 * Map module name (URL format) to permission module name
 * Used for converting route module names to permission format
 * Example: 'access-logs' -> 'ACCESS_LOGS', 'control-panel' -> 'CONTROL_PANEL'
 */
export function getModuleFromRouteName(routeName: string): string | null {
  if (!routeName) return null;
  
  const moduleMap: Record<string, string> = {
    'access-logs': 'ACCESS_LOGS',
    'entities': 'ENTITIES',
    'properties': 'PROPERTIES',
    'control-panel': 'CONTROL_PANEL',
    'conversations': 'CONVERSATIONS',
    'profile': 'PROFILE',
    'people': 'PEOPLE',
    'person-details': 'PEOPLE',
  };

  return moduleMap[routeName] || routeName.toUpperCase().replace(/-/g, '_');
}

/**
 * Get module-level VIEW permission from route name
 * Example: 'access-logs' -> 'ACCESS_LOGS:VIEW'
 */
export function getModuleViewPermission(routeName: string): string | null {
  const module = getModuleFromRouteName(routeName);
  return module ? `${module}:VIEW` : null;
}

/**
 * Check if permissions are loaded (not empty array)
 * Used to determine if permission checks should proceed
 */
export function arePermissionsLoaded(permissions: string[]): boolean {
  return Array.isArray(permissions) && (
    permissions.length > 0 || 
    permissions.includes('*') || 
    permissions.includes('SUPER_ADMIN_PERMISSIONS')
  );
}

/**
 * Safe permission check - validates permissions array before checking
 * Prevents errors when permissions array is not yet loaded
 * 
 * @param requiredPermissions - Array of required permissions
 * @param userPermissions - User's permissions array
 * @returns true if user has any of the required permissions, false otherwise
 */
export function safeHasAnyPermission(
  requiredPermissions: string[], 
  userPermissions: string[]
): boolean {
  if (!Array.isArray(userPermissions) || userPermissions.length === 0) {
    return false;
  }
  return hasAnyPermission(requiredPermissions, userPermissions);
}

/**
 * Check if an error is a permission-related 403 error
 * Permission errors are distinct from authentication errors
 * 
 * @param error - Error object from API response or catch block
 * @returns true if the error is a permission error, false otherwise
 */
export function isPermissionError(error: any): boolean {
  if (!error) return false;

  // Check for 403 status code (from axios response)
  const statusCode = error?.response?.status || error?.errCode;
  if (statusCode !== 403) return false;

  // Get error message
  const errorMessage = error?.errMessage || error?.response?.data?.errMessage || error?.message || '';
  
  // Permission errors specifically contain "Access denied" and "Required permission" or "permission"
  const isPermissionErrorFound = errorMessage.includes('Access denied') && 
                           (errorMessage.includes('Required permission') || errorMessage.includes('permission'));
  
  return isPermissionErrorFound;
}

/**
 * Handle permission error by redirecting to 403 page
 * Use this in catch blocks to handle permission errors consistently
 * 
 * @param error - Error object from API response or catch block
 * @returns true if error was handled (redirected), false otherwise
 */
export function handlePermissionError(error: any): boolean {
  if (!isPermissionError(error)) return false;

  // Redirect to 403 page
  if (typeof window !== 'undefined') {
    window.location.replace('/403');
  }
  
  return true;
}

